import React, { Fragment, useState } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { FaTimes, FaChevronDown } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';

// Service items with their respective paths - some will have subitems in the future
const serviceItems = [
  { 
    label: 'Mutual Funds', 
    path: '/services/mutual-funds',
    // Placeholder for future subitems
    children: []
  },
  { 
    label: 'Investment Management', 
    path: '/services/investment-management',
    // Placeholder for future subitems  
    children: []
  },
  { 
    label: 'Personal Retirement Account', 
    path: '/services/personal-retirement-account',
    // Placeholder for future subitems
    children: []
  },
  { 
    label: 'Securities Trading & Brokerage', 
    path: '/services/securities-trading-brokerage',
    // Placeholder for future subitems
    children: []
  },
  { 
    label: 'Trustee And Registrar Services', 
    path: '/services/trustee-registrar-services',
    // Placeholder for future subitems
    children: []
  },
  { 
    label: 'Wealth Management', 
    path: '/services/wealth-management',
    // Placeholder for future subitems
    children: []
  }
];

const navItems = [
  { label: 'About Us', path: '/about' },
  { label: 'Services', children: serviceItems },
  { 
    label: 'Digital Assets', 
    children: [
      { label: 'Digital Assets Investment', path: 'https://digitalassets.segwaycapital.com' },
      { label: 'Futures Trading', path: 'https://digitalassets.segwaycapital.com/futures-trading' },
      { label: 'Earn Crypto', path: 'https://digitalassets.segwaycapital.com/earn-crypto' },
      { label: 'Tax Reporting', path: 'https://digitalassets.segwaycapital.com/tax-reporting' },
      { label: 'Web3', path: 'https://digitalassets.segwaycapital.com/web3' },
      { label: 'Buy Crypto', path: 'https://digitalassets.segwaycapital.com/buy-crypto' },
      { label: 'Exchange', path: 'https://digitalassets.segwaycapital.com/exchange' },
    ]
  },
  { label: 'Contact Us', path: '/contact' },
  { label: 'Apply Now', path: '/apply-now', isButton: true }
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const MobileSidebar = ({ open, onClose }) => {
  const [openMenus, setOpenMenus] = useState([]);
  const location = useLocation();
  const navigate = useNavigate();

  // Reset open menus when sidebar closes
  React.useEffect(() => {
    if (!open) setOpenMenus([]);
  }, [open]);

  const handleNav = (path) => {
    onClose();
    // Check if it's an external URL
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    } else {
      setTimeout(() => {
        navigate(path);
        window.scrollTo(0, 0);
      }, 100);
    }
  };

  const toggleMenu = (label) => {
    setOpenMenus((prev) =>
      prev.includes(label)
        ? prev.filter((l) => l !== label)
        : [...prev, label]
    );
  };

  // Recursive render for nested menus
  const renderMenu = (items, level = 0) => (
    <ul className={level === 0 ? 'space-y-1' : 'ml-4 border-l border-gray-200 dark:border-gray-700 pl-2 space-y-1'}>
      {items.map((item) => {
        const isActive = location.pathname === item.path;
        const hasChildren = !!(item.children && item.children.length > 0);
        const isOpen = openMenus.includes(item.label);
        
        return (
          <li key={item.label}>
            <div className={classNames(
              'flex items-center',
              level === 0 ? 'py-2' : 'py-1'
            )}>
              {item.path ? (
                <button
                  onClick={() => handleNav(item.path)}
                  className={classNames(
                    'flex-1 text-left rounded-lg transition-colors px-4',
                    item.isButton
                      ? 'bg-secondary text-black hover:bg-secondary-light font-semibold'
                      : isActive 
                        ? 'text-secondary font-semibold' 
                        : 'text-[#333] dark:text-white hover:text-secondary hover:bg-gray-100 dark:hover:bg-gray-800',
                    level === 0 ? 'py-3' : 'py-2',
                  )}
                >
                  {item.label}
                </button>
              ) : (
                <span className="flex-1 px-4 py-2 text-[#333] dark:text-white font-medium">{item.label}</span>
              )}
              {hasChildren && (
                <button
                  onClick={() => toggleMenu(item.label)}
                  className="p-2 focus:outline-none"
                  aria-label={`Toggle ${item.label} submenu`}
                >
                  <FaChevronDown className={classNames('transition-transform duration-300', isOpen ? 'rotate-180' : '')} />
                </button>
              )}
            </div>
            {hasChildren && isOpen && (
              <div>{renderMenu(item.children, level + 1)}</div>
            )}
          </li>
        );
      })}
    </ul>
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-50 md:hidden" onClose={onClose}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100"
          leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black bg-opacity-50 transition-opacity" />
        </Transition.Child>
        <div className="fixed inset-0 flex justify-end">
          <Transition.Child
            as={Fragment}
            enter="transform transition ease-in-out duration-300"
            enterFrom="translate-x-full" enterTo="translate-x-0"
            leave="transform transition ease-in-out duration-200"
            leaveFrom="translate-x-0" leaveTo="translate-x-full"
          >
            <Dialog.Panel className="w-[280px] max-w-full h-full bg-white dark:bg-black shadow-xl flex flex-col">
              <div className="flex justify-end p-4">
                <button onClick={onClose} className="text-secondary focus:outline-none">
                  <FaTimes size={24} />
                </button>
              </div>
              <nav className="flex-1 px-2 pb-4 overflow-y-auto">
                {renderMenu(navItems)}
              </nav>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default MobileSidebar; 